<template>
  <section class="qilin-avatar" :class="`qilin-avatar--${displayMood}`">
    <div class="qilin-avatar__glow"></div>
    <div class="qilin-avatar__stage">ร่างที่ {{ pet.stage || 1 }}</div>
    <button
      class="qilin-avatar__pet-stage"
      :class="{ 'qilin-avatar__pet-stage--jump': tapping }"
      type="button"
      :aria-label="pet.name ? `เล่นกับกิเลน ${pet.name}` : 'เล่นกับกิเลนของฉัน'"
      @click="petTap"
    >
      <span
        v-for="(particle, index) in moodParticles"
        :key="`${displayMood}-${index}`"
        class="qilin-avatar__particle"
        :class="`qilin-avatar__particle--${index + 1}`"
        aria-hidden="true"
      >{{ particle }}</span>
      <img
        :key="`${pet.gender || 'male'}-${pet.stage || 1}-${displayMood}`"
        class="qilin-avatar__image"
        :src="image"
        :alt="pet.name ? `กิเลน ${pet.name}` : 'กิเลนของฉัน'"
        width="560"
        height="560"
      >
    </button>
    <div class="qilin-avatar__message">{{ moodMessage }}</div>
    <button
      class="qilin-avatar__play"
      type="button"
      :disabled="pet.playedToday || loading"
      @click="$emit('play')"
    >
      <span aria-hidden="true">{{ pet.playedToday ? '✓' : '♡' }}</span>
      {{ pet.playedToday ? 'เล่นด้วยกันแล้ววันนี้' : loading ? 'กำลังเล่น...' : 'เล่นด้วยกัน +5 XP' }}
    </button>
  </section>
</template>

<script>
import { qilinPetImage } from '../../config/qilinAssets'

export default {
  name: 'QilinAvatar',
  props: {
    pet: { type: Object, required: true },
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      tapping: false,
      tapTimer: null
    }
  },
  computed: {
    displayMood () {
      const moods = ['new', 'happy', 'content', 'hungry', 'sulking']
      return moods.indexOf(String(this.pet.mood || '')) >= 0 ? this.pet.mood : 'new'
    },
    allowAnimation () {
      return !window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    image () {
      return qilinPetImage(this.pet, this.allowAnimation)
    },
    moodMessage () {
      const messages = {
        new: 'ตั้งชื่อให้เรา แล้วมาเติบโตไปด้วยกันนะ',
        happy: 'วันนี้อิ่มแล้ว มีความสุขที่สุดเลย!',
        content: 'ยังอารมณ์ดีอยู่ แวะมาหาอีกนะ',
        hungry: 'เริ่มหิวแล้ว ฝากครบ 100 บาทเพื่อให้อาหารเรานะ',
        sulking: 'คิดถึงจัง... กลับมาให้อาหารเราหน่อยนะ'
      }
      return messages[this.displayMood] || messages.new
    },
    moodParticles () {
      const sets = {
        new: ['✨', '💛', '✨'],
        happy: ['✨', '💛', '✨'],
        content: ['✨', '🎵', '✨'],
        hungry: ['🍖', '🦴', '🍚'],
        sulking: ['💤', '💧', '💤']
      }
      return sets[this.displayMood] || sets.new
    }
  },
  beforeDestroy () {
    if (this.tapTimer) window.clearTimeout(this.tapTimer)
  },
  methods: {
    petTap () {
      if (this.tapping) return
      this.tapping = true
      this.$emit('pet-tap')
      this.tapTimer = window.setTimeout(() => {
        this.tapping = false
        this.tapTimer = null
      }, 700)
    }
  }
}
</script>

<style scoped>
.qilin-avatar {
  position: relative;
  display: flex;
  min-height: 520px;
  align-items: center;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  padding: 22px 20px 24px;
  border: 1px solid rgba(255, 218, 112, .36);
  border-radius: 28px;
  background: radial-gradient(circle at 50% 35%, rgba(92, 220, 187, .28), transparent 43%), linear-gradient(160deg, #10285b, #080c31 62%, #03051c);
  box-shadow: 0 24px 60px rgba(0, 0, 0, .32);
}
.qilin-avatar__glow {
  position: absolute;
  top: 11%;
  left: 50%;
  width: 340px;
  height: 340px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(251, 200, 68, .26), rgba(52, 211, 153, .10) 48%, transparent 70%);
}
.qilin-avatar__stage {
  position: absolute;
  z-index: 2;
  top: 18px;
  right: 18px;
  padding: 7px 12px;
  border: 1px solid rgba(255, 218, 112, .3);
  border-radius: 999px;
  background: rgba(3, 5, 28, .56);
  color: #ffe58c;
  font-size: 12px;
  font-weight: 800;
}
.qilin-avatar__pet-stage {
  position: relative;
  z-index: 1;
  display: grid;
  width: min(100%, 430px);
  min-height: 360px;
  place-items: end center;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.qilin-avatar__pet-stage::before {
  position: absolute;
  right: 14%;
  bottom: 8px;
  left: 14%;
  height: 52px;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(251, 200, 68, .28), transparent 70%);
  content: '';
  filter: blur(7px);
}
.qilin-avatar__pet-stage:focus-visible {
  border-radius: 24px;
  box-shadow: 0 0 0 3px rgba(255, 229, 140, .75);
}
.qilin-avatar__image {
  position: relative;
  z-index: 1;
  width: min(100%, 390px);
  height: auto;
  object-fit: contain;
  transform-origin: 50% 100%;
  animation: qilin-idle 3.4s ease-in-out infinite;
  filter: drop-shadow(0 18px 20px rgba(0, 0, 0, .35));
  will-change: transform;
}
.qilin-avatar--content .qilin-avatar__image { animation-duration: 4.4s; }
.qilin-avatar--hungry .qilin-avatar__image { animation: qilin-sad 3.6s ease-in-out infinite; }
.qilin-avatar--sulking .qilin-avatar__image { animation: qilin-sad 4.5s ease-in-out infinite; }
.qilin-avatar__pet-stage--jump .qilin-avatar__image { animation: qilin-jump .7s cubic-bezier(.28, .84, .42, 1); }
.qilin-avatar__particle {
  position: absolute;
  z-index: 3;
  opacity: 0;
  pointer-events: none;
  font-size: 32px;
  filter: drop-shadow(0 4px 7px rgba(1, 4, 24, .62));
  animation: qilin-particle-float 3s ease-in-out infinite;
  will-change: transform, opacity;
}
.qilin-avatar__particle--1 { top: 33%; left: 4%; animation-delay: -.2s; }
.qilin-avatar__particle--2 { top: 45%; right: 3%; font-size: 27px; animation-delay: -1.4s; }
.qilin-avatar__particle--3 { top: 5%; left: 25%; font-size: 24px; animation-delay: -2.8s; }
.qilin-avatar--hungry .qilin-avatar__particle { animation-duration: 3.4s; }
.qilin-avatar--sulking .qilin-avatar__particle { animation-duration: 4.4s; }
@keyframes qilin-idle {
  0%, 100% { transform: translateY(0) rotate(-1.5deg); }
  25% { transform: translateY(-3px) rotate(0deg) scaleY(1.01); }
  50% { transform: translateY(-9px) rotate(1.5deg); }
  75% { transform: translateY(-3px) rotate(0deg) scaleY(.99); }
}
@keyframes qilin-sad {
  0%, 100% { transform: translateY(0) scaleY(1); }
  50% { transform: translateY(2px) scaleY(.985); }
}
@keyframes qilin-jump {
  0% { transform: translateY(0) scale(1); }
  20% { transform: translateY(3px) scaleX(1.1) scaleY(.88); }
  50% { transform: translateY(-34px) scaleX(.94) scaleY(1.08) rotate(3deg); }
  75% { transform: translateY(0) scaleX(1.05) scaleY(.94); }
  100% { transform: translateY(0) scale(1); }
}
@keyframes qilin-particle-float {
  0%, 100% { opacity: 0; transform: translateY(10px) scale(.6); }
  35% { opacity: 1; }
  70% { opacity: 0; transform: translateY(-24px) scale(1.15); }
}
@keyframes qilin-tap-reduced {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(.96); }
}
.qilin-avatar__message {
  position: relative;
  z-index: 2;
  margin: -8px auto 15px;
  color: rgba(255, 255, 255, .78);
  font-size: 14px;
  text-align: center;
}
.qilin-avatar__play {
  position: relative;
  z-index: 2;
  min-height: 46px;
  padding: 0 20px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffd85d, #f39a32);
  box-shadow: 0 10px 24px rgba(243, 154, 50, .24);
  color: #171329;
  font-family: inherit;
  font-size: 14px;
  font-weight: 900;
}
.qilin-avatar__play:disabled { box-shadow: none; filter: grayscale(.35); opacity: .62; }
.qilin-avatar--sulking { filter: saturate(.82); }
@media (max-width: 767px) {
  .qilin-avatar { min-height: 430px; border-radius: 22px; }
  .qilin-avatar__pet-stage { min-height: 300px; }
  .qilin-avatar__image { width: min(100%, 320px); }
}
@media (prefers-reduced-motion: reduce) {
  .qilin-avatar__image { animation: none; }
  .qilin-avatar__particle { opacity: .92; animation: none; }
  .qilin-avatar__pet-stage--jump .qilin-avatar__image { animation: qilin-tap-reduced .28s ease-out; }
}
</style>
